import React, { useContext, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingContext, M_Table, NavigateContext, BufferService } from 'src/M_Components'
import { DataTable } from 'primereact/datatable/datatable.cjs.js'
import { Column } from 'primereact/column/column.cjs.js'
import { MovementGroupDialog } from './movementGroupDialog'
import './equipmentAndMovementList.css'

const EMPTY_FILTER = { prefix: '', number: '', checkDigit: '', equipmentType: '' }

const buildFilterString = (form) => {
    const parts = []
    if (form.prefix.trim())        parts.push(`contains(Prefix, '${encodeURIComponent(form.prefix.trim())}')`)
    if (form.number.trim())        parts.push(`Number eq ${form.number.trim()}`)
    if (form.checkDigit.trim())    parts.push(`CheckDigit eq ${form.checkDigit.trim()}`)
    if (form.equipmentType.trim()) parts.push(`contains(EquipmentType.Code, '${encodeURIComponent(form.equipmentType.trim())}')`)
    return parts.join(' and ')
}

const EquipmentFilter = ({ onFilterChange }) => {
    const { t } = useTranslation()
    const [form, setForm] = useState(EMPTY_FILTER)
    const [isCollapsed, setIsCollapsed] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const [hasActiveFilters, setHasActiveFilters] = useState(false)

    const handleChange = useCallback((key, value) => {
        setForm(prev => ({ ...prev, [key]: value }))
        setIsDirty(true)
    }, [])

    const handleSubmit = () => {
        const filterString = buildFilterString(form)
        onFilterChange(filterString)
        setIsDirty(false)
        setHasActiveFilters(!!filterString)
    }

    const handleClear = () => {
        setForm(EMPTY_FILTER)
        onFilterChange('')
        setIsDirty(false)
        setHasActiveFilters(false)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSubmit()
    }

    const filterFields = [
        { key: 'prefix',        label: 'Prefix'   },
        { key: 'number',        label: 'Number'   },
        { key: 'checkDigit',    label: 'CD'       },
        { key: 'equipmentType', label: 'Eq. Type' },
    ]

    return (
        <div className="eq-filter-panel">
            <div
                className={`eq-filter-header ${isCollapsed ? 'collapsed' : ''}`}
                onClick={() => setIsCollapsed(p => !p)}
            >
                <div className="eq-filter-header-left">
                    <i className='pi pi-filter' style={{ fontSize: '0.9rem', color: '#6c757d' }} />
                    <span className="eq-filter-title">{t('Filters')}</span>
                    {hasActiveFilters && !isDirty && (
                        <span className="eq-filter-badge">{t('Active')}</span>
                    )}
                    {isDirty && (
                        <span className="eq-filter-badge" style={{ background: '#fff3cd', color: '#856404' }}>
                            {t('Unsaved')}
                        </span>
                    )}
                </div>
                <div className="eq-filter-header-right">
                    {hasActiveFilters && (
                        <button className="eq-filter-clear" onClick={e => { e.stopPropagation(); handleClear() }}>
                            {t('Clear')}
                        </button>
                    )}
                    <i className={`pi ${isCollapsed ? 'pi-chevron-down' : 'pi-chevron-up'}`} style={{ fontSize: '0.8rem', color: '#6c757d' }} />
                </div>
            </div>
            {!isCollapsed && (
                <>
                    <div className="eq-filter-body">
                        {filterFields.map(({ key, label }) => (
                            <div key={key} className="eq-filter-field">
                                <label className="eq-filter-label">{t(label)}</label>
                                <input
                                    className={`eq-filter-input ${form[key] ? 'active' : ''}`}
                                    value={form[key]}
                                    onChange={e => handleChange(key, e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={t(label)}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="movement-filter-footer">
                        <button
                            className={`movement-filter-search-btn ${isDirty ? 'dirty' : ''}`}
                            onClick={handleSubmit}
                        >
                            <i className="pi pi-search" style={{ fontSize: '0.85rem' }} />
                            {t('Search')}
                        </button>
                    </div>
                </>
            )}
        </div>
    )
}

export const EquipmentAndMovementList = ({ roles }) => {
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)
    const ctx2 = useContext(NavigateContext)

    const [activeFilter, setActiveFilter] = useState('')
    const [equipmentPanels, setEquipmentPanels] = useState(new Map())
    const [refresh, setRefresh] = useState(false)
    const [selectedEquipmentRows, setSelectedEquipmentRows] = useState([])
    const [showGroupDialog, setShowGroupDialog] = useState(false)

    const isFirstRender = useRef(true)
    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return }
        setRefresh(true)
    }, [activeFilter])

    const newRoute = (url, operation, label, row) => {
        if (operation === 'Edit' || operation === 'Copy') {
            if (row) {
                sessionStorage.setItem('editingEquipment', JSON.stringify(row))
                sessionStorage.setItem('operationType', operation)
            } else {
                return
            }
        }
        if (operation === 'Create') ctx.setPassValues({ Operation: operation })
        ctx2.newTab(url, label)
    }

    const yearBuiltColumn = (rowData) => {
        if (!rowData.YearOfManufacture) return ''
        if (typeof rowData.YearOfManufacture === 'string') {
            const year = rowData.YearOfManufacture.split('-')[0]
            if (year === '1900') return ''
            return year
        }
        return rowData.YearOfManufacture || ''
    }

    // Formats ISO datetime as "YYYY-MM-DD HH:mm"
    const formatDateTime = (isoString) => {
        if (!isoString) return ''
        const [datePart, timePart] = isoString.split('T')
        if (!timePart) return datePart
        const [hours, minutes] = timePart.split(':')
        return `${datePart} ${hours}:${minutes}`
    }

    // ─────────────────────────────────────────────────────────────────
    // Navigate to Add movement — pre-fills equipment fields from row
    // ─────────────────────────────────────────────────────────────────
    const handleAddMovement = (equipmentRow) => {
        sessionStorage.setItem('operationType', 'Create')
        // Pre-fill equipment so LastMovementCrud can read it on mount
        sessionStorage.setItem('prefilledEquipment', JSON.stringify({
            equipmentId:       equipmentRow.Id,
            prefix:            equipmentRow.Prefix            ?? '',
            number:            equipmentRow.Number?.toString() ?? '',
            cd:                equipmentRow.CheckDigit?.toString() ?? '',
            equipmentTypeId:   equipmentRow.EquipmentTypeId?.toString() ?? '',
            equipmentTypeName: equipmentRow.EquipmentTypeCode ?? '',
            payload:           equipmentRow.Payload?.toString() ?? '',
            insideLength:      equipmentRow.InsideLength?.toString() ?? '',
        }))
        ctx2.newTab(
            'containerControl/lastMovementCrud',
            `${t('Add')} ${t('Movement')}`
        )
    }

    // ─────────────────────────────────────────────────────────────────
    // Navigate to Edit movement (was right-click, now a button)
    // ─────────────────────────────────────────────────────────────────
    const handleEditMovement = (movement) => {
        sessionStorage.setItem('editingMovement', JSON.stringify({ Id: movement.Id }))
        sessionStorage.setItem('operationType', 'Edit')
        ctx2.newTab(
            'containerControl/lastMovementCrud',
            `${t('Edit')} ${t('Movement')}`
        )
    }

    const handleRowSelect = (rows) => {
        if (!rows || rows.length === 0) {
            setEquipmentPanels(new Map())
            setSelectedEquipmentRows([])
            return
        }

        setSelectedEquipmentRows(rows)

        const currentIds = new Set(rows.map(r => r.Id))

        setEquipmentPanels(prev => {
            const next = new Map(prev)
            for (const id of next.keys()) {
                if (!currentIds.has(id)) next.delete(id)
            }
            return next
        })

        rows.forEach(row => {
            setEquipmentPanels(prev => {
                if (prev.has(row.Id)) return prev

                const next = new Map(prev)
                next.set(row.Id, { row, history: [], loading: true })

                const movementService = new BufferService('Movement', 'secondary')
                movementService.getId(`byequipment/${row.Id}`).then(data => {
                    if (data?.name === 'AxiosError') {
                        ctx.toastMessages('error', data.message, '')
                        setEquipmentPanels(p => {
                            const updated = new Map(p)
                            updated.set(row.Id, { row, history: [], loading: false })
                            return updated
                        })
                        return
                    }
                    setEquipmentPanels(p => {
                        const updated = new Map(p)
                        updated.set(row.Id, {
                            row,
                            history: Array.isArray(data) ? data : [],
                            loading: false
                        })
                        return updated
                    })
                })

                return next
            })
        })
    }

    const closePanel = (equipmentId) => {
        setEquipmentPanels(prev => {
            const next = new Map(prev)
            next.delete(equipmentId)
            return next
        })
    }

    const getEquipmentLabel = (row) =>
        `${row.Prefix ?? ''}${row.Number ?? ''}-${row.CheckDigit ?? ''}`

    // Guard — requires at least 2 equipment rows selected
    const handleAddGroupMovement = (rows) => {
        if (!rows || rows.length < 2) {
            ctx.toastMessages('error', '', t('Please select at least 2 equipment rows for group movement'))
            return
        }
        setSelectedEquipmentRows(rows)
        setShowGroupDialog(true)
    }

    const handleGroupSuccess = () => {
        setRefresh(true)
    }

    // ─────────────────────────────────────────────────────────────────
    // Action buttons column rendered per movement row
    // ─────────────────────────────────────────────────────────────────
    const movementActionsBody = (movement) => (
        <button
            className="eq-history-action-btn eq-history-edit-btn"
            title={t('Edit')}
            onClick={() => handleEditMovement(movement)}
        >
            <i className="pi pi-pencil" style={{ fontSize: '0.8rem' }} />
            <span>{t('Edit')}</span>
        </button>
    )

    // ─────────────────────────────────────────────────────────────────
    // Empty state shown when a selected equipment has no movements
    // ─────────────────────────────────────────────────────────────────
    const EmptyMovementState = ({ row }) => (
        <div className="eq-history-empty-state">
            <div className="eq-history-empty-icon">
                <i className="pi pi-inbox" />
            </div>
            <p className="eq-history-empty-title">{t('No movements found')}</p>
            <p className="eq-history-empty-subtitle">
                {t('No movements registered for')} <strong>{getEquipmentLabel(row)}</strong>
            </p>
            <button
                className="eq-history-add-btn eq-history-add-btn--empty"
                onClick={() => handleAddMovement(row)}
            >
                <i className="pi pi-plus" style={{ fontSize: '0.85rem' }} />
                {t('Add Movement')}
            </button>
        </div>
    )

    return (
        <>
            <M_Table
                entityName={'Equipment'}
                api='secondary'
                showTitle={true}
                title={'Equipment and Movements'}
                roleName={'usersGroups'}
                rolePermissions={roles}
                refresh={refresh}
                setRefresh={setRefresh}
                filterComponent={<EquipmentFilter onFilterChange={setActiveFilter} />}
                replaceFunctions={new Map([
                    ['Create', (row) => newRoute('containerControl/equipmentAndMovementCrud', 'Create', `${t('Add')} ${t('Equipment')}`, row)],
                    ['Edit',   (row) => newRoute('containerControl/equipmentAndMovementCrud', 'Edit',   `${t('Edit')} ${t('Equipment')}`, row)],
                    ['Copy',   (row) => newRoute('containerControl/equipmentAndMovementCrud', 'Copy',   `${t('Copy')} ${t('Equipment')}`, row)],
                    ['AddGroupMovement', (rows) => handleAddGroupMovement(rows)],
                ])}
                columnTemplates={[
                    { field: 'YearOfManufacture', body: yearBuiltColumn, header: t('Year Built') }
                ]}
                onPassLine={handleRowSelect}
                FILTER={activeFilter || undefined}
            />

            {[...equipmentPanels.values()].map(({ row, history, loading }) => (
                <div key={row.Id} className="eq-history-panel">
                    {/* Panel header — always visible */}
                    <div className="eq-history-header">
                        <span className="eq-history-title">
                            {t('Movement History')} — {getEquipmentLabel(row)}
                        </span>
                        <div className="eq-history-header-actions">
                            {/* Add button: pre-fills equipment and opens CRUD in Create mode */}
                            <button
                                className="eq-history-add-btn"
                                onClick={() => handleAddMovement(row)}
                                title={t('Add Movement')}
                            >
                                <i className="pi pi-plus" style={{ fontSize: '0.8rem' }} />
                                {t('Add')}
                            </button>
                            <button className="eq-history-close" onClick={() => closePanel(row.Id)}>✕</button>
                        </div>
                    </div>

                    {/* Panel body */}
                    {loading ? (
                        <div className="eq-history-loading">{t('Loading')}...</div>
                    ) : history.length === 0 ? (
                        /* ── Empty state ── */
                        <EmptyMovementState row={row} />
                    ) : (
                        /* ── Movement table ── */
                        <DataTable
                            value={history}
                            size="small"
                            stripedRows
                            emptyMessage={t('No movements found')}
                            style={{ fontSize: '0.875rem' }}
                        >
                            {/* Actions column — Edit button per row */}
                            <Column
                                header={t('Actions')}
                                body={movementActionsBody}
                                style={{ width: '80px', textAlign: 'center' }}
                            />
                            <Column field="Date" header={t('Date')} body={(r) => formatDateTime(r.Date)} style={{ width: '140px' }} />
                            <Column field="MovementTypeCode" header={t('Move')} style={{ width: '80px' }} />
                            <Column field="Status" header={t('Status')} />
                            <Column field="EquipmentServiceCode" header={t('Service')} style={{ width: '90px' }} />
                            <Column field="EquipmentConditionCode" header={t('Condition')} style={{ width: '100px' }} />
                            <Column field="MovementFromCode" header={t('From')} style={{ width: '100px' }} />
                            <Column field="MovementToCode" header={t('To')} style={{ width: '100px' }} />
                            <Column field="MovementFinalCode" header={t('Final')} style={{ width: '100px' }} />
                            <Column field="TransportName" header={t('Transport')} />
                            <Column field="VoyageNumber" header={t('Voyage')} style={{ width: '90px' }} />
                            <Column field="Comments" header={t('Comments')} />
                        </DataTable>
                    )}
                </div>
            ))}

            <MovementGroupDialog
                open={showGroupDialog}
                setOpen={setShowGroupDialog}
                mode='create'
                selectedRows={selectedEquipmentRows}
                onSuccess={handleGroupSuccess}
            />
        </>
    )
}